import { CheckCircle, Star, Crown } from 'lucide-react';

const ICON_MAP = {
  'check-circle': CheckCircle,
  'star': Star,
  'crown': Crown,
};

const TIER_CONFIG = {
  student: {
    verified: { label: 'Verified Student', color: '#2563EB', bg: '#DBEAFE', icon: 'check-circle' },
    premium: { label: 'Rising Star', color: '#7C3AED', bg: '#EDE9FE', icon: 'star' },
    elite: { label: 'Top Talent', color: '#D97706', bg: '#FEF3C7', icon: 'crown' },
  },
  agent: {
    verified: { label: 'Verified Agent', color: '#2563EB', bg: '#DBEAFE', icon: 'check-circle' },
    premium: { label: 'Trusted Advisor', color: '#7C3AED', bg: '#EDE9FE', icon: 'star' },
    elite: { label: 'Elite Partner', color: '#D97706', bg: '#FEF3C7', icon: 'crown' },
  },
  institution: {
    verified: { label: 'Verified', color: '#2563EB', bg: '#DBEAFE', icon: 'check-circle' },
    premium: { label: 'Premium', color: '#7C3AED', bg: '#EDE9FE', icon: 'star' },
    elite: { label: 'Trusted Partner', color: '#D97706', bg: '#FEF3C7', icon: 'crown' },
  },
};

/**
 * TrustBadge — renders the subscription-linked trust badge.
 *
 * Props:
 *  - tier: "verified" | "premium" | "elite" | null
 *  - role: "student" | "agent" | "institution"
 *  - size: "xs" | "sm" | "md" | "lg"  (default "sm")
 *  - showLabel: boolean (default false) — show text label next to icon
 *  - inGrace: boolean — show dimmed style during grace period
 *  - variant: "default" | "light" — light variant for dark backgrounds
 *  - className: extra classes
 */
export default function TrustBadge({ tier, role = 'student', size = 'sm', showLabel = false, inGrace = false, variant = 'default', className = '' }) {
  if (!tier) return null;

  const roleTiers = TIER_CONFIG[role] || TIER_CONFIG.student;
  const config = roleTiers[tier];
  if (!config) return null;

  const IconComponent = ICON_MAP[config.icon] || CheckCircle;

  const sizes = {
    xs: { icon: 'h-3 w-3', text: 'text-[9px]', pill: 'px-1.5 py-0.5 gap-0.5' },
    sm: { icon: 'h-3.5 w-3.5', text: 'text-[10px]', pill: 'px-2 py-0.5 gap-1' },
    md: { icon: 'h-4 w-4', text: 'text-xs', pill: 'px-2.5 py-1 gap-1' },
    lg: { icon: 'h-5 w-5', text: 'text-sm', pill: 'px-3 py-1.5 gap-1.5' },
  };

  const s = sizes[size] || sizes.sm;
  const isLight = variant === 'light';

  if (!showLabel) {
    return (
      <span
        title={config.label}
        className={`inline-flex items-center ${inGrace ? 'opacity-50' : ''} ${className}`}
        data-testid={`trust-badge-${tier}`}
      >
        <IconComponent className={s.icon} style={{ color: isLight ? '#ffffff' : config.color }} />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center ${s.pill} rounded-full font-semibold ${s.text} ${inGrace ? 'opacity-50' : ''} ${className}`}
      style={isLight
        ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }
        : { backgroundColor: config.bg, color: config.color }}
      data-testid={`trust-badge-${tier}`}
    >
      <IconComponent className={s.icon} />
      {config.label}
    </span>
  );
}

/**
 * Utility to get badge config for a role+tier (useful in lists/tables)
 */
export function getBadgeConfig(role, tier) {
  if (!tier) return null;
  const roleTiers = TIER_CONFIG[role] || TIER_CONFIG.student;
  return roleTiers[tier] || null;
}
